import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import webBackApi from '../../../api/webBackApi';
import { getDailyImages } from '../../api/iframeConfig.api';

// Chequeo de encuadre entre dos fotos de la misma cámara, todo en el navegador.
//
// Si la cámara se movió (la reapuntaron, la cambiaron de lugar) el "Antes y
// Ahora" compara dos escenas distintas y no sirve. Acá:
//   1. Se bajan las dos fotos a 128×72 en escala de grises y se compara el
//      mapa de bordes (gradiente), que aguanta cambios de luz y de obra mejor
//      que los píxeles crudos, con correlación cruzada normalizada (NCC).
//   2. Se busca el corrimiento (±10 % en x, ±10 % en y) y una escala chica
//      (0,95 / 1 / 1,05) que maximiza la NCC, y se refina a 256×144.
//   3. "Mismo encuadre" = el pico es alto o se destaca del resto (PSR). Los
//      umbrales salen de calibrar con cámaras reales (fijas, que se sacuden y
//      que se movieron): ver el PR.
// Si se movió, hay que encontrar la primera foto que sí coincide con la de ahora:
//   a) PRECALCULADO (instantáneo): un job diario (lanzamiento-servidores/9-encuadres, misma cuenta que acá)
//      guarda los tramos de encuadre de cada cámara; GET /api/encuadres/:feedId.
//   b) si no hay precálculo o está viejo: búsqueda binaria en el navegador sobre los días con fotos
//      (con barra de progreso), y el resultado queda en localStorage para no repetirla.

const CW = 128;
const CH = 72;
const FW = 256;
const FH = 144;
const featureCache = new Map();
const SS_PREFIX = 'lisual-encuadre:';
const LS_PREFIX = 'lisual-encuadre:v1:';
const LS_DIAS = 14; // lo que vale un resultado guardado de la búsqueda del navegador
const ENCUADRES_BASE = (process.env.REACT_APP_ENCUADRES_API_URL || process.env.REACT_APP_ENTREGAS_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081').replace(/\/$/, '');

const urlKey = (url) => {
  try {
    const u = new URL(url, window.location.href);
    return u.host + u.pathname;
  } catch (e) {
    return String(url).split('?')[0];
  }
};

// En local el bucket no deja leer píxeles desde localhost (CORS): se pasa por
// el proxy /s3img de setupProxy.js. En producción pro.lisual.com está permitido.
const analysisSrc = (url) => {
  if (process.env.NODE_ENV === 'development') {
    try {
      const u = new URL(url);
      if (/\.amazonaws\.com$/.test(u.host)) return `/s3img/${u.host}${u.pathname}${u.search}`;
    } catch (e) { /* url relativa */ }
  }
  return url;
};

const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.decoding = 'async';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('no se pudo cargar la foto'));
  img.src = analysisSrc(url);
});

const gradientMap = (img, W, H) => {
  // Dos pasos de achique para no tener aliasing al bajar de 4K a 128 px.
  const mid = document.createElement('canvas');
  mid.width = W * 4;
  mid.height = H * 4;
  const mctx = mid.getContext('2d');
  mctx.imageSmoothingQuality = 'high';
  mctx.drawImage(img, 0, 0, mid.width, mid.height);
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(mid, 0, 0, W, H);
  const { data } = ctx.getImageData(0, 0, W, H); // tira si el canvas quedó "tainted"
  const g = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) g[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  const out = new Float32Array(W * H);
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const gx = g[i + 1] - g[i - 1];
      const gy = g[i + W] - g[i - W];
      out[i] = Math.sqrt(gx * gx + gy * gy);
    }
  }
  return out;
};

const getFeatures = async (url) => {
  const key = urlKey(url);
  if (!featureCache.has(key)) {
    featureCache.set(key, loadImage(url).then((img) => ({
      coarse: gradientMap(img, CW, CH),
      fine: gradientMap(img, FW, FH),
    })));
  }
  try {
    return await featureCache.get(key);
  } catch (e) {
    featureCache.delete(key);
    throw e;
  }
};

// NCC entre A (ahora) y B (antes) con B corrido (dx, dy) px y escalado s
// alrededor del centro: B'(p) = B(c + (p - c - d) / s).
const ncc = (A, B, W, H, s, dx, dy) => {
  const cx = (W - 1) / 2;
  const cy = (H - 1) / 2;
  let n = 0; let sa = 0; let sb = 0; let saa = 0; let sbb = 0; let sab = 0;
  for (let y = 0; y < H; y++) {
    const sy = Math.round(cy + (y - cy - dy) / s);
    if (sy < 0 || sy >= H) continue;
    const row = y * W;
    const srow = sy * W;
    for (let x = 0; x < W; x++) {
      const sx = Math.round(cx + (x - cx - dx) / s);
      if (sx < 0 || sx >= W) continue;
      const a = A[row + x];
      const b = B[srow + sx];
      n++; sa += a; sb += b; saa += a * a; sbb += b * b; sab += a * b;
    }
  }
  if (n < W * H * 0.5) return -1;
  const cov = sab - (sa * sb) / n;
  const va = saa - (sa * sa) / n;
  const vb = sbb - (sb * sb) / n;
  return cov / Math.sqrt(va * vb + 1e-9);
};

export const isSameFraming = (score, psr) => score >= 0.5 || (score >= 0.3 && psr >= 4.5) || (score >= 0.36 && psr >= 4.0);

export function matchFeatures(after, before) {
  let best = { score: -2, s: 1, dx: 0, dy: 0 };
  const atOne = [];
  for (const s of [1, 0.95, 1.05]) {
    for (let dy = -7; dy <= 7; dy++) {
      for (let dx = -13; dx <= 13; dx++) {
        const v = ncc(after.coarse, before.coarse, CW, CH, s, dx, dy);
        if (s === 1) atOne.push(v);
        if (v > best.score) best = { score: v, s, dx, dy };
      }
    }
  }
  const mean = atOne.reduce((a, b) => a + b, 0) / atOne.length;
  const std = Math.sqrt(atOne.reduce((a, b) => a + (b - mean) ** 2, 0) / atOne.length) || 1;
  const psr = (best.score - mean) / std;
  // Refinado al doble de resolución alrededor del mejor.
  let fine = { score: -2, s: best.s, dx: best.dx * 2, dy: best.dy * 2 };
  for (const s of [best.s - 0.02, best.s, best.s + 0.02]) {
    for (let dy = best.dy * 2 - 2; dy <= best.dy * 2 + 2; dy++) {
      for (let dx = best.dx * 2 - 2; dx <= best.dx * 2 + 2; dx++) {
        const v = ncc(after.fine, before.fine, FW, FH, s, dx, dy);
        if (v > fine.score) fine = { score: v, s, dx, dy };
      }
    }
  }
  const dxf = fine.dx / FW;
  const dyf = fine.dy / FH;
  const shift = Math.max(Math.abs(dxf), Math.abs(dyf));
  const same = isSameFraming(best.score, psr);
  let status = 'moved';
  if (same && shift < 0.012 && Math.abs(fine.s - 1) < 0.015) status = 'same';
  else if (same && shift <= 0.08 && Math.abs(fine.s - 1) <= 0.08) status = 'aligned';
  return { status, score: best.score, psr, dx: dxf, dy: dyf, s: fine.s };
}

const pairKey = (a, b) => `${SS_PREFIX}par:${urlKey(a)}|${urlKey(b)}`;
const ssGet = (k) => { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } };
const ssSet = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* lleno o bloqueado */ } };

export async function compareFraming(afterUrl, beforeUrl) {
  const k = pairKey(afterUrl, beforeUrl);
  const cached = ssGet(k);
  if (cached) return cached;
  const [a, b] = await Promise.all([getFeatures(afterUrl), getFeatures(beforeUrl)]);
  const r = matchFeatures(a, b);
  ssSet(k, r);
  return r;
}

// Primer índice i en [lo, hi] con pred(i) verdadero, suponiendo que pred es
// monótono (falso... falso, verdadero... verdadero) y que pred(hi) es
// verdadero. Prueba `k` puntos en paralelo por ronda: con 30 días son 3 rondas
// en vez de 5 descargas una detrás de otra.
async function firstTrue(lo, hi, pred, k = 4) {
  while (lo < hi) {
    const span = hi - lo;
    const step = Math.max(1, Math.floor(span / (k + 1)));
    const probes = [];
    for (let i = lo + step - 1; i < hi && probes.length < k; i += step) probes.push(i);
    if (!probes.length) probes.push(lo);
    const results = await Promise.all(probes.map((i) => pred(i)));
    const firstOk = results.findIndex(Boolean);
    if (firstOk === -1) lo = probes[probes.length - 1] + 1;
    else {
      hi = probes[firstOk];
      if (firstOk > 0) lo = probes[firstOk - 1] + 1;
    }
  }
  return lo;
}

const dayKey = (d) => (d ? String(d).slice(0, 10) : '');

// Días con fotos entre dos fechas (una foto representativa por día), con el
// mismo endpoint mensual que usa el calendario.
async function listDays(feedId, fromDay, toDay) {
  const out = new Map();
  const start = new Date(`${fromDay}T12:00:00Z`);
  const end = new Date(`${toDay}T12:00:00Z`);
  const months = [];
  for (let d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1)); d <= end; d.setUTCMonth(d.getUTCMonth() + 1)) {
    months.push([d.getUTCFullYear(), d.getUTCMonth() + 1]);
  }
  const results = await Promise.all(months.map(([year, month]) => webBackApi
    .get(`/api/s3-images/${feedId}/monthly`, { params: { year, month } })
    .then((r) => (Array.isArray(r.data) ? r.data : []))
    .catch(() => [])));
  results.flat().forEach((item) => {
    const url = typeof item === 'string' ? item : item?.url;
    if (!url) return;
    const m = url.match(/(\d{4})\/(\d{2})\/(\d{2})/);
    const day = m ? `${m[1]}-${m[2]}-${m[3]}` : dayKey(item?.date);
    if (!day || day < fromDay || day > toDay) return;
    if (!out.has(day)) out.set(day, []);
    out.get(day).push({ url, date: item?.date || day });
  });
  return [...out.keys()].sort().map((day) => {
    const items = out.get(day);
    return { day, image: items[Math.floor(items.length / 2)] };
  });
}

// Ruta de S3 de una foto (sin firma), para guardar y volver a firmar.
const keyDeUrl = (url) => {
  try { return decodeURIComponent(new URL(url, window.location.href).pathname.replace(/^\//, '')); } catch (e) { return ''; }
};

// La URL firmada de una foto a partir de su key: se busca en las fotos del día (misma API que el calendario).
async function fotoPorKey(feedId, day, key) {
  if (!key) return null;
  const list = (await getDailyImages(feedId, day).catch(() => [])) || [];
  const it = list
    .map((x) => (typeof x === 'string' ? { url: x, date: day } : { url: x?.url, date: x?.date || day }))
    .find((x) => x.url && keyDeUrl(x.url).endsWith(key));
  return it || null;
}

const lsGet = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } };
const lsSet = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
    // Tope: no más de 60 búsquedas guardadas (se borran las más viejas).
    const keys = Object.keys(localStorage).filter((x) => x.startsWith(`${LS_PREFIX}desde:`));
    if (keys.length > 60) keys.map((x) => [x, lsGet(x)?.t || 0]).sort((p, q) => p[1] - q[1]).slice(0, keys.length - 60).forEach(([x]) => localStorage.removeItem(x));
  } catch (e) { /* lleno o bloqueado */ }
};

// Precalculado de la cámara (una vez por sesión). null si no hay, si falla o si la API no está.
const precalcCache = new Map();
export function leerPrecalculado(feedId) {
  if (!precalcCache.has(feedId)) {
    let token = null;
    try { token = localStorage.getItem('authLisualToken'); } catch (e) { /* sin acceso */ }
    precalcCache.set(feedId, axios
      .get(`${ENCUADRES_BASE}/api/encuadres/${encodeURIComponent(feedId)}`, { timeout: 6000, headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.data?.disponible && r.data?.tramoActual ? r.data : null))
      .catch(() => null));
  }
  return precalcCache.get(feedId);
}

// "Ahora" del par precalculado: la foto con la que el job verificó que el encuadre coincide (tramoActual.refKey).
// Rayos X y Antes y Ahora la usan en vez de la última foto: si la cámara se movió después del cálculo, igual el par
// que se ve está alineado. -> { url, date } | null (sin precalculado vigente se sigue usando la última foto).
export async function ahoraPrecalculado(feedId) {
  const pre = await leerPrecalculado(feedId);
  const t = pre && pre.vigente !== false ? pre.tramoActual : null;
  return t?.refUrl ? { url: t.refUrl, date: t.refFecha || `${t.refDia}T15:00:00Z` } : null;
}

// Con el precalculado: ¿hay que mover la foto de "antes" al primer día del encuadre de hoy? -> { image, changeDay } | null
export async function earliestFromPrecalc(feedId, before, after) {
  const pre = await leerPrecalculado(feedId);
  if (!pre || pre.vigente === false) return null;
  const t = pre.tramoActual;
  const bd = dayKey(before?.date);
  const ad = dayKey(after?.date);
  if (!bd || !ad || bd >= t.desde || ad < t.desde) return null; // la de antes ya es del encuadre de hoy
  const tramo = (pre.tramos || []).find((x) => x.desde <= bd && bd <= x.hasta);
  if (tramo?.coincideConActual) return null; // ese día ya tenía el mismo encuadre que hoy
  // El backend ya la manda firmada (primeraFotoUrl): al instante, sin pedir todas las fotos de ese día.
  const image = t.primeraFotoUrl
    ? { url: t.primeraFotoUrl, date: t.primeraFotoFecha || `${t.desde}T12:00:00Z` }
    : await fotoPorKey(feedId, t.desde, t.primeraFotoKey);
  return image ? { image, changeDay: t.desde, fuente: 'precalculado' } : null;
}

// Resultado guardado de una búsqueda anterior en este navegador (sin buscar de nuevo) -> { image, changeDay } | null
export async function earliestFromStorage(feedId, after, fromDay) {
  const g = lsGet(`${LS_PREFIX}desde:${feedId}:${fromDay}`);
  if (!g || !(g.toDay <= dayKey(after?.date)) || Date.now() - g.t > LS_DIAS * 864e5) return null;
  const image = Date.now() - g.t < 45 * 60e3 && g.url ? { url: g.url, date: g.date } : await fotoPorKey(feedId, g.day, g.key);
  return image ? { image, changeDay: g.changeDay, fuente: 'guardado' } : null;
}

// Primera foto (desde fromDay) con el mismo encuadre que `after`. Supone que
// el encuadre cambió una vez y después quedó fijo: búsqueda binaria por día y
// después dentro del día. onProgress(0..1) va avisando cuánto falta.
// El resultado queda en localStorage (LS_DIAS días): la próxima vez no se busca.
export async function findEarliestSameFraming(feedId, after, fromDay, { onProgress = () => {}, sinCache = false } = {}) {
  const toDay = dayKey(after.date);
  const lsKey = `${LS_PREFIX}desde:${feedId}:${fromDay}`;
  if (!sinCache) {
    const g = await earliestFromStorage(feedId, after, fromDay);
    if (g) return g;
  }
  onProgress(0.02);
  const days = await listDays(feedId, fromDay, toDay);
  onProgress(0.08);
  if (!days.length) return null;
  // Progreso real: fotos comparadas sobre las que hacen falta (4 por ronda; ~log5 rondas por días y por fotos del día).
  const rondas = (n) => (n > 0 ? Math.ceil(Math.log(n + 1) / Math.log(5)) : 0);
  let hechas = 0;
  let total = 1 + 4 * rondas(days.length - 1) + 4 * rondas(30);
  const ok = async (img) => {
    try {
      const r = await compareFraming(after.url, img.url);
      return r.status !== 'moved' ? r : null;
    } catch (e) {
      return null;
    } finally {
      hechas += 1;
      onProgress(0.08 + 0.9 * Math.min(1, hechas / total));
    }
  };
  const last = days.length - 1;
  if (!(await ok(days[last].image))) return null;
  const day = days[await firstTrue(0, last, async (i) => Boolean(await ok(days[i].image)))];
  let chosen = day.image;
  try {
    const list = (await getDailyImages(feedId, day.day)) || [];
    const photos = list
      .map((it) => (typeof it === 'string' ? { url: it, date: day.day } : { url: it?.url, date: it?.date || day.day }))
      .filter((p) => p.url);
    let b = photos.findIndex((p) => urlKey(p.url) === urlKey(day.image.url));
    if (b < 0) b = photos.length - 1;
    total = hechas + 1 + 4 * rondas(b);
    if (b >= 0 && (await ok(photos[b]))) {
      chosen = photos[await firstTrue(0, b, async (i) => Boolean(await ok(photos[i])))];
    }
  } catch (e) { /* nos quedamos con la representativa del día */ }
  onProgress(1);
  const result = { image: chosen, changeDay: day.day, fuente: 'busqueda' };
  lsSet(lsKey, { key: keyDeUrl(chosen.url), url: chosen.url, date: chosen.date, day: day.day, changeDay: day.day, toDay, t: Date.now() });
  return result;
}

const formatDay = (day) => {
  const [y, m, d] = String(day).slice(0, 10).split('-');
  return d && m ? `${d}/${m}/${y}` : '';
};

// CSS para aplicar a la capa "antes" (en coordenadas de la foto, así funciona
// también con cámaras rotadas).
export const alignmentTransform = (alignment, baseTransform) => {
  if (!alignment || alignment.status !== 'aligned') return baseTransform;
  const t = `translate(${(alignment.dx * 100).toFixed(2)}%, ${(alignment.dy * 100).toFixed(2)}%) scale(${alignment.s.toFixed(3)})`;
  return baseTransform ? `${baseTransform} ${t}` : t;
};

// Hook compartido por "Antes y Ahora" y "Rayos X".
//  - Al cargar con la primera foto por defecto: si el precalculado dice que la cámara cambió de encuadre
//    después de esa foto, se pasa directo a la primera foto del encuadre de hoy (sin esperar).
//  - Si no hay precalculado: se chequea el par y, si no coincide, se busca en el navegador (con progreso).
//  - Cuando el usuario elige una fecha a mano: chequea y avisa / alinea.
export function useFramingCheck({ feedId, before, after, setBefore, isDefaultBefore }) {
  const [state, setState] = useState({ checking: false, alignment: null, notice: null, warning: null, progress: null });
  const originalRef = useRef(null);
  const forcedRef = useRef(false);
  const runRef = useRef(0);
  const autoRef = useRef(null); // de dónde salió la foto "antes" puesta automáticamente: precalculado | guardado | busqueda

  // El precalculado se pide apenas se abre la pestaña, en paralelo con las fotos.
  useEffect(() => { if (feedId) leerPrecalculado(feedId); }, [feedId]);

  useEffect(() => {
    if (!feedId || !before?.url || !after?.url) return undefined;
    const run = ++runRef.current;
    let alive = true;
    const vigente = () => alive && runRef.current === run;
    const done = (patch) => { if (vigente()) setState((s) => ({ ...s, checking: false, searching: false, progress: null, ...patch })); };
    const usar = (found) => {
      originalRef.current = originalRef.current || before;
      autoRef.current = found.fuente;
      // Con el precalculado se muestra ya (el chequeo del par nuevo, que sólo afina la alineación, corre
      // por detrás). Si salió de una búsqueda, seguimos en "checking" hasta que el efecto vuelva a correr
      // con la foto nueva (resultado ya cacheado), así no se ve un cuadro con el par viejo en el medio.
      const ya = found.fuente === 'precalculado' || found.fuente === 'guardado';
      setState((st) => ({ ...st, checking: !ya, searching: false, progress: null, alignment: null, warning: null, notice: { changeDay: found.changeDay, text: `La cámara cambió de encuadre el ${formatDay(found.changeDay)}; comparamos desde esa fecha.` } }));
      setBefore(found.image, { auto: true });
    };
    // Recién puesta desde el precalculado: el chequeo no bloquea la vista.
    const enSegundoPlano = (autoRef.current === 'precalculado' || autoRef.current === 'guardado') && isDefaultBefore && !forcedRef.current;
    if (!enSegundoPlano) setState((s) => ({ ...s, checking: true }));
    (async () => {
      // 24/10: si hay precalculado vigente, manda él: las fotos se ven al instante y nunca se busca en el navegador
      // (antes, si la comparación del navegador no coincidía con el precalculado, arrancaba la búsqueda con barra).
      const precalc = isDefaultBefore && !forcedRef.current ? await leerPrecalculado(feedId).catch(() => null) : null;
      const confiar = !!precalc && precalc.vigente !== false;
      // 1) Precalculado (o lo que ya encontró una búsqueda anterior en este navegador): instantáneo.
      if (isDefaultBefore && !forcedRef.current && !autoRef.current) {
        const pre = (await earliestFromPrecalc(feedId, before, after).catch(() => null))
          || (confiar ? null : await earliestFromStorage(feedId, after, dayKey(before.date)).catch(() => null));
        if (!vigente()) return;
        if (pre && keyDeUrl(pre.image.url) !== keyDeUrl(before.url)) { usar(pre); return; }
      }
      if (confiar) {
        // Mismo encuadre según el precalculado: se muestra ya y la comparación sólo afina la alineación, por detrás.
        done({ alignment: null, warning: null });
        compareFraming(after.url, before.url).then((r) => { if (vigente() && r.status !== 'moved') setState((s) => ({ ...s, alignment: r })); }).catch(() => {});
        return;
      }
      let r;
      try {
        r = await compareFraming(after.url, before.url);
      } catch (e) {
        done({ alignment: null, warning: null }); // sin acceso a los píxeles: mostramos igual
        return;
      }
      if (!vigente()) return;
      if (r.status !== 'moved') {
        done({ alignment: r, warning: null });
        return;
      }
      if (forcedRef.current) {
        done({ alignment: null, warning: 'Las fotos no coinciden: la cámara cambió de encuadre entre estas fechas.' });
        return;
      }
      // 2) Búsqueda en el navegador (o el resultado guardado de una búsqueda anterior). Si la foto puesta
      //    sola ya salió de una búsqueda y tampoco coincide, no se vuelve a buscar (sin vueltas infinitas).
      if (isDefaultBefore && autoRef.current !== 'busqueda') {
        // (si el precalculado quedó viejo y la foto no coincide, se busca igual, ahora sí bloqueando)
        setState((s) => ({ ...s, checking: true, searching: true, progress: 0, notice: null }));
        const desde = dayKey((originalRef.current || before).date);
        const found = await findEarliestSameFraming(feedId, after, desde, {
          sinCache: autoRef.current === 'guardado',
          onProgress: (p) => { if (vigente()) setState((s) => ({ ...s, progress: Math.max(s.progress || 0, p) })); },
        }).catch(() => null);
        if (!vigente()) return;
        if (found?.image && keyDeUrl(found.image.url) !== keyDeUrl(before.url)) { usar(found); return; }
      }
      done({ alignment: null, warning: 'Las fotos no coinciden: la cámara cambió de encuadre entre estas fechas.' });
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId, before?.url, after?.url]);

  const showOriginal = useCallback(() => {
    if (!originalRef.current) return;
    forcedRef.current = true;
    setState((s) => ({ ...s, notice: null }));
    setBefore(originalRef.current, { auto: true });
  }, [setBefore]);

  const userPicked = useCallback(() => {
    forcedRef.current = false;
    originalRef.current = null;
    autoRef.current = null;
    setState((s) => ({ ...s, notice: null, warning: null }));
  }, []);

  return { ...state, showOriginal, userPicked, hasOriginal: Boolean(originalRef.current) };
}
